import { Children, useMemo, forwardRef } from 'react';
import type { ForwardedRef } from 'react';
import { cn, setThisClass } from '@lib/utils';
import { getPathStyles } from './getPathStyles';
import { randomId } from '@lib/utils/randomId';
import type { ColorType } from '@lib/types';

interface GradientConfig {
  start: string;
  end: string;
}

type CirclebarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type StrokeWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_MAP: Record<CirclebarSize, number> = {
  xs: 14,
  sm: 20,
  md: 24,
  lg: 28,
  xl: 32,
};

const STROKE_WIDTH_MAP: Record<StrokeWidth, number> = {
  xs: 2,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 10,
};

type CirclebarOwnProps = {
  offsetDegree?: number;
  gapDegree?: number;
  gapOffsetDegree?: number;
  strokeWidth?: StrokeWidth;
  strokeLinecap?: 'butt' | 'round' | 'square';
  isActive?: boolean;
  size?: CirclebarSize;
  showRail?: boolean;
  color?: ColorType;
  contentProps?: React.HTMLAttributes<HTMLDivElement>;
  rootProps?: React.HTMLAttributes<HTMLDivElement>;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  className?: string;
  classNames?: {
    root?: string;
    wrapper?: string;
    svg?: string;
    content?: string;
  };
  /** Content to display inside the circle */
  children?: React.ReactNode;
};

/**
 * Props for the Circlebar component with all variations.
 *
 * Requirements:
 * 1. When `isIndeterminate` is false/undefined, `value` is required
 * 2. When `variant` is "gradient", `gradient` config is required
 */
export type CirclebarProps =
  // When indeterminate is true, value is optional
  | (CirclebarOwnProps & {
      /**
       * Set to true for an indeterminate loading state.
       * When true, the `value` prop becomes optional.
       */
      isIndeterminate: true;
      /**
       * Current progress value (0-100).
       * Optional when in indeterminate mode.
       */
      value?: number;
      /** Visual style variant */
      variant?: 'default' | 'soft';
      /**
       * Gradient configuration (optional for default/soft variants)
       */
      gradient?: GradientConfig;
    })
  | (CirclebarOwnProps & {
      /**
       * Set to true for an indeterminate loading state.
       * When true, the `value` prop becomes optional.
       */
      isIndeterminate: true;
      /**
       * Current progress value (0-100).
       * Optional when in indeterminate mode.
       */
      value?: number;
      /**
       * Visual style variant.
       * When set to "gradient", requires gradient config.
       */
      variant: 'gradient';
      /**
       * Gradient configuration.
       * REQUIRED when variant is "gradient".
       */
      gradient: GradientConfig;
    })
  // When indeterminate is false/undefined, value is required
  | (CirclebarOwnProps & {
      /**
       * Set to false for a determinate progress circle.
       * When false, the `value` prop is required.
       */
      isIndeterminate?: false;
      /**
       * Current progress value (0-100).
       * REQUIRED when not in indeterminate mode.
       */
      value: number;
      /** Visual style variant */
      variant?: 'default' | 'soft';
      /**
       * Gradient configuration (optional for default/soft variants)
       */
      gradient?: GradientConfig;
    })
  | (CirclebarOwnProps & {
      /**
       * Set to false for a determinate progress circle.
       * When false, the `value` prop is required.
       */
      isIndeterminate?: false;
      /**
       * Current progress value (0-100).
       * REQUIRED when not in indeterminate mode.
       */
      value: number;
      /**
       * Visual style variant.
       * When set to "gradient", requires gradient config.
       */
      variant: 'gradient';
      /**
       * Gradient configuration.
       * REQUIRED when variant is "gradient".
       */
      gradient: GradientConfig;
    });

/**
 * Circlebar Component
 * @requires value - Required when isIndeterminate is false/undefined
 * @requires gradient - Required when variant is "gradient"
 */

export const Circlebar = forwardRef<HTMLDivElement, CirclebarProps>(
  (
    {
      value,
      isIndeterminate = false,
      offsetDegree,
      gapDegree = 0,
      gapOffsetDegree = 0,
      strokeWidth = 'md',
      strokeLinecap = 'round',
      isActive = false,
      size = 'md',
      showRail = true,
      children,
      color = 'neutral',
      variant = 'default',
      contentProps = {},
      rootProps = {},
      wrapperProps = {},
      className,
      classNames = {},
      gradient,
      ...rest
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const sizeValue = SIZE_MAP[size];
    const strokeWidthValue = STROKE_WIDTH_MAP[strokeWidth];
    if (!isIndeterminate && value === undefined) {
      console.error(
        `Circlebar Error: 'value' prop is required when 'isIndeterminate' is false.
      Please provide a number value between 0-100 to display the progress circle.`
      );
    }

    if (variant === 'gradient' && !gradient) {
      console.error(
        `Circlebar Error: 'gradient' prop is required when 'variant' is "gradient".
      Please provide a gradient config with { start: string, end: string } format.`
      );
    }

    if (value !== undefined && (value < 0 || value > 100)) {
      console.warn(
        `Circlebar Warning: 'value' should be between 0 and 100, got ${value}.
      Values outside this range may cause unexpected display issues.`
      );
    }

    const gradientId = `gradient-${randomId()}`;
    const viewBoxSize = 100 + strokeWidthValue;

    const { pathString: railPathString, pathStyle: railPathStyle } = useMemo(
      () => getPathStyles(100, 0, gapDegree, strokeWidthValue),
      [gapDegree, strokeWidthValue]
    );

    const { pathString: fillPathString, pathStyle: fillPathStyle } = useMemo(
      () => getPathStyles(value || 0, offsetDegree, gapDegree, strokeWidthValue),
      [gapDegree, offsetDegree, strokeWidthValue, value]
    );

    // Stroke classes for the progress path
    const getStrokeClass = () => {
      if (variant === 'gradient') return '';
      if (color === 'neutral') return 'stroke-fg-muted';
      return cn(setThisClass(color), 'stroke-this');
    };

    // Rail stroke classes
    const getRailStrokeClass = () => {
      if (color === 'neutral' || variant !== 'soft') {
        return 'stroke-surface-raised';
      }
      return cn(setThisClass(color), 'stroke-this-bg');
    };

    return (
      <div className={cn('max-w-full', classNames?.root)} {...rootProps} ref={ref}>
        <div
          {...wrapperProps}
          className={cn('relative inline-block align-middle', classNames?.wrapper)}
          style={{ width: `${sizeValue / 4}rem`, height: `${sizeValue / 4}rem` }}
        >
          <svg
            style={{
              transform: gapOffsetDegree ? `rotate(${gapOffsetDegree}deg)` : undefined,
            }}
            viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
            className={cn(
              isIndeterminate && 'animate-[spin_2s_linear_infinite]',
              className,
              classNames?.svg
            )}
            {...rest}
          >
            {showRail && (
              <path
                d={railPathString}
                strokeWidth={strokeWidthValue}
                strokeLinecap={strokeLinecap}
                fill="none"
                style={railPathStyle}
                className={getRailStrokeClass()}
              />
            )}

            {!isIndeterminate ? (
              <>
                <path
                  d={fillPathString}
                  strokeWidth={strokeWidthValue}
                  strokeLinecap={strokeLinecap}
                  fill="none"
                  style={{
                    ...fillPathStyle,
                    transitionProperty: 'stroke-dasharray',
                    transitionDuration: '200ms',
                  }}
                  className={cn('ease-out', getStrokeClass())}
                  stroke={variant === 'gradient' ? `url(#${gradientId})` : undefined}
                />

                {isActive && (
                  <path
                    d={fillPathString}
                    strokeWidth={strokeWidthValue}
                    strokeLinecap={strokeLinecap}
                    fill="none"
                    style={{
                      ...fillPathStyle,
                      ['--dashoffset' as string]: `${((value || 0) / 100) * (Math.PI * 100 - gapDegree)}px`,
                      transformOrigin: 'center',
                      transform: `rotate(${(gapDegree / 2) * 1.15}deg)`,
                    }}
                    className="circlebar-active-path stroke-white"
                  />
                )}
              </>
            ) : (
              <circle
                cx={viewBoxSize / 2}
                cy={viewBoxSize / 2}
                r="50"
                fill="none"
                strokeWidth={strokeWidthValue}
                className={cn(
                  'animate-[circlebar-indeterminate_1.5s_linear_infinite]',
                  getStrokeClass()
                )}
                stroke={variant === 'gradient' ? `url(#${gradientId})` : undefined}
              />
            )}

            {variant === 'gradient' && gradient && (
              <defs>
                <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: gradient.start, stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: gradient.end, stopOpacity: 1 }} />
                </linearGradient>
              </defs>
            )}
          </svg>
          {Children.count(children) > 0 && (
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center',
                classNames?.content
              )}
              {...contentProps}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Circlebar.displayName = 'Circlebar';
